import { useTranslation } from "react-i18next";

import { useGetFaqQuery, useGetFaqPublicQuery } from "../app/api/private/faq";
import { FaqCategory } from "../app/models/FaqCategory";
import { Accordion } from "../features/shared/Accordion";
import { Container } from "../features/shared/Container";
import { Loader } from "../features/shared/Loader";
import { Paper } from "../features/shared/Paper";
import { Markdown } from "../features/shared/Markdown";
import { MainPage } from "./shared/MainPage";
import { ContactCard } from "../features/shared/ContactCard";
import { Button } from "../features/shared/Button";

import { router } from "../app/services/router";

import { ReactComponent as Icon } from "./FaqPageIcon.svg";
import { ReactComponent as MessagingIcon } from "./FaqPageMessagingIcon.svg";
import { ReactComponent as InterlocutorsIcon } from "./FaqPageInterlocutorsIcon.svg";
import "./FaqPage.scss";

const classNamePrefix = "FaqPage";

type FaqPageProps = { isLoggedIn?: boolean };

export const FaqPage = (props: FaqPageProps) => {
  const { isLoggedIn } = props;

  const { currentData: faqData, error, isFetching } = useGetFaqQuery();
  const { currentData: faqDataPublic } = useGetFaqPublicQuery();

  const dataToDisplay: FaqCategory[] =
    (isLoggedIn ? faqData : faqDataPublic) ?? [];

  return faqData ? (
    <FaqPageContent data={dataToDisplay} isLoggedIn={isLoggedIn} />
  ) : (
    <Loader error={error} isFetching={isFetching} />
  );
};

type FaqPageContentProps = { data: FaqCategory[]; isLoggedIn?: boolean };

const FaqPageContent = (props: FaqPageContentProps) => {
  const { data: dataToDisplay, isLoggedIn } = props;

  const { t } = useTranslation();
  const { meta, title, contactCard } = t("pages.FaqPage") as unknown as {
    meta: { title: string };
    title: string;
    contactCard: {
      title: string;
      text: string;
      action: {
        messaging: string;
        interlocutors: string;
        info: string;
      };
    };
  };

  const hash = window.location.hash.slice(1);
  const hashItemId = hash.startsWith("faq-") ? hash.replace("faq-", "") : null;

  return (
    <MainPage
      dataLayer={{
        categorie_page: "faq",
        nom_page: "faq",
        virtualpagetitle: "FAQ",
      }}
      icon={<Icon />}
      meta={meta}
      title={meta.title}
    >
      <Container>
        <Paper className={`${classNamePrefix}-panel`}>
          <div className={`${classNamePrefix}-intro`}>
            <Markdown>{title}</Markdown>
          </div>
          <div className={`${classNamePrefix}-content`}>
            {dataToDisplay.map((category) => (
              <div key={category.id} className={`${classNamePrefix}-category`}>
                <h2 className={`${classNamePrefix}-category-title`}>
                  {category.title}
                </h2>
                <div className={`${classNamePrefix}-sub-accordions`}>
                  {category.items.map((item) => (
                    <div key={item.id} id={`faq-${item.id}`}>
                      <Accordion
                        title={item.question}
                        titleAs="h3"
                        triggerLabel={`Expand ${item.question}`}
                        className={`${classNamePrefix}-sub-accordion`}
                        defaultOpen={hashItemId === item.id}
                      >
                        <Paper className={`${classNamePrefix}-answer`}>
                          <div
                            dangerouslySetInnerHTML={{ __html: item.answer }}
                          />
                        </Paper>
                      </Accordion>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Paper>
        <ContactCard
          className={`${classNamePrefix}-ContactCard`}
          title={contactCard.title}
        >
          <p className={`${classNamePrefix}-ContactCard-text`}>
            {contactCard.text}
          </p>
          {isLoggedIn ? (
            <div>
              <Button
                dataLayer={{
                  eventAction: "clic",
                  eventCategory: "faq",
                  eventLabel: "ma-messagerie",
                }}
                onClick={() => {
                  window.location.href = router.getTo("mainMessaging");
                }}
                variant="action"
              >
                <MessagingIcon />
                {contactCard.action.messaging}
              </Button>
              <Button
                dataLayer={{
                  eventAction: "clic",
                  eventCategory: "faq",
                  eventLabel: "contacter-un-conseiller",
                }}
                level="secondary"
                onClick={() => {
                  window.location.href = router.getTo(
                    "mainContactInterlocutors",
                  );
                }}
                variant="action"
              >
                <InterlocutorsIcon />
                {contactCard.action.interlocutors}
              </Button>
            </div>
          ) : (
            <div>
              <p className={`${classNamePrefix}-ContactCard-info`}>
                {contactCard.action.info}
              </p>
            </div>
          )}
        </ContactCard>
      </Container>
    </MainPage>
  );
};
